import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import systemSettingsAPI from '../../api/systemSettingsAPI';
import ChartOfAccountsSearch from '../../components/ChartOfAccountsSearch';

// ─── Tiny account picker row ──────────────────────────────────────────────────
function AccountRow({ label, value, onSelect }) {
  const [clearTrigger, setClearTrigger] = useState(false);
  const displayValue = value
    ? `${value.accountNumber} — ${value.accountName}`
    : '';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-start py-2 border-b border-gray-100 last:border-0">
      <label className="text-sm text-gray-600 pt-2">{label}</label>
      <div className="sm:col-span-2">
        <ChartOfAccountsSearch
          value={displayValue}
          onSelect={(acct) => onSelect(acct)}
          clearTrigger={clearTrigger}
          placeholder="Search accounts…"
          showLinkedBadge={false}
        />
        {value && (
          <p className="text-xs text-teal-600 mt-0.5">
            {value.accountNumber} — {value.accountName}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Helper: build display object from raw populated account ──────────────────
function toDisplay(acct) {
  if (!acct) return null;
  if (typeof acct === 'object' && acct.accountNumber) return acct;
  return null;
}

const currentYear = String(new Date().getFullYear());

export default function SystemSettingsPage() {
  const [settings, setSettings] = useState({ minimumBillingAmount: 250, truckingSOACounters: {} });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [soaYear, setSoaYear] = useState(currentYear);

  // GL mapping: stores { _id, accountNumber, accountName } objects (or null)
  const [glMapping, setGlMapping] = useState({
    arAccount: null,
    paymentMethods: { cash: null, bankTransfer: null, gcash: null, other: null },
    deductions: {
      missing: null, damage: null, discount: null,
      generalExpense: null, withheld2307: null, others: null,
    },
  });
  const [glSaving, setGlSaving] = useState(false);
  const [glSaved, setGlSaved] = useState(false);
  const [glLoading, setGlLoading] = useState(true);

  // Waybill GL mapping state
  const [wbMapping, setWbMapping] = useState({
    arAccount: null,
    trucking: { salesAccount: null, strippingExpenseAccount: null },
    freightforwarding: { salesAccount: null },
  });
  const [wbSaving, setWbSaving] = useState(false);
  const [wbSaved, setWbSaved] = useState(false);
  const [wbLoading, setWbLoading] = useState(true);

  // Check if any GL mapping slot is unconfigured
  const missingMappings = (() => {
    const missing = [];
    if (!glMapping.arAccount) missing.push('AR Account');
    if (!glMapping.paymentMethods.cash) missing.push('Payment: Cash');
    if (!glMapping.paymentMethods.bankTransfer) missing.push('Payment: Bank Transfer');
    if (!glMapping.paymentMethods.gcash) missing.push('Payment: GCash');
    if (!glMapping.paymentMethods.other) missing.push('Payment: Other');
    if (!glMapping.deductions.missing) missing.push('Deduction: Missing');
    if (!glMapping.deductions.damage) missing.push('Deduction: Damage');
    if (!glMapping.deductions.discount) missing.push('Deduction: Discount');
    if (!glMapping.deductions.generalExpense) missing.push('Deduction: General Expense');
    if (!glMapping.deductions.withheld2307) missing.push('Deduction: Withheld 2307');
    if (!glMapping.deductions.others) missing.push('Deduction: Others');
    return missing;
  })();

  useEffect(() => {
    systemSettingsAPI.get()
      .then(r => setSettings(r.data?.data || { minimumBillingAmount: 250, truckingSOACounters: {} }))
      .catch(() => {});

    systemSettingsAPI.getWaybillGLMapping()
      .then(r => {
        const raw = r.data?.data || {};
        setWbMapping({
          arAccount:         toDisplay(raw.arAccount),
          trucking:          {
            salesAccount:            toDisplay(raw.trucking?.salesAccount),
            strippingExpenseAccount: toDisplay(raw.trucking?.strippingExpenseAccount),
          },
          freightforwarding: { salesAccount: toDisplay(raw.freightforwarding?.salesAccount) },
        });
      })
      .catch(() => {})
      .finally(() => setWbLoading(false));

    systemSettingsAPI.getPaymentGLMapping()
      .then(r => {
        const raw = r.data?.data || {};
        setGlMapping({
          arAccount: toDisplay(raw.arAccount),
          paymentMethods: {
            cash:         toDisplay(raw.paymentMethods?.cash),
            bankTransfer: toDisplay(raw.paymentMethods?.bankTransfer),
            gcash:        toDisplay(raw.paymentMethods?.gcash),
            other:        toDisplay(raw.paymentMethods?.other),
          },
          deductions: {
            missing:        toDisplay(raw.deductions?.missing),
            damage:         toDisplay(raw.deductions?.damage),
            discount:       toDisplay(raw.deductions?.discount),
            generalExpense: toDisplay(raw.deductions?.generalExpense),
            withheld2307:   toDisplay(raw.deductions?.withheld2307),
            others:         toDisplay(raw.deductions?.others),
          },
        });
      })
      .catch(() => {})
      .finally(() => setGlLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const soaLastNumber = settings.truckingSOACounters?.[soaYear];
      await systemSettingsAPI.update({
        minimumBillingAmount: settings.minimumBillingAmount,
        ...(soaLastNumber != null ? { truckingSOAYear: soaYear, truckingSOALastNumber: soaLastNumber } : {}),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch(e) { console.error(e); }
    finally { setSaving(false); }
  };

  const setPaymentAccount = (method, acct) => {
    setGlMapping(prev => ({
      ...prev,
      paymentMethods: { ...prev.paymentMethods, [method]: acct },
    }));
  };

  const setDeductionAccount = (type, acct) => {
    setGlMapping(prev => ({
      ...prev,
      deductions: { ...prev.deductions, [type]: acct },
    }));
  };

  const handleWbGLSave = async () => {
    setWbSaving(true);
    try {
      const payload = {
        arAccount: wbMapping.arAccount?._id || null,
        trucking:          {
          salesAccount:            wbMapping.trucking.salesAccount?._id            || null,
          strippingExpenseAccount: wbMapping.trucking.strippingExpenseAccount?._id || null,
        },
        freightforwarding: { salesAccount: wbMapping.freightforwarding.salesAccount?._id || null },
      };
      await systemSettingsAPI.updateWaybillGLMapping(payload);
      setWbSaved(true);
      setTimeout(() => setWbSaved(false), 3000);
    } catch(e) { console.error(e); }
    finally { setWbSaving(false); }
  };

  const handleGLSave = async () => {
    setGlSaving(true);
    try {
      // Send only IDs to the backend
      const payload = {
        arAccount: glMapping.arAccount?._id || null,
        paymentMethods: {
          cash:         glMapping.paymentMethods.cash?._id         || null,
          bankTransfer: glMapping.paymentMethods.bankTransfer?._id || null,
          gcash:        glMapping.paymentMethods.gcash?._id        || null,
          other:        glMapping.paymentMethods.other?._id        || null,
        },
        deductions: {
          missing:        glMapping.deductions.missing?._id        || null,
          damage:         glMapping.deductions.damage?._id         || null,
          discount:       glMapping.deductions.discount?._id       || null,
          generalExpense: glMapping.deductions.generalExpense?._id || null,
          withheld2307:   glMapping.deductions.withheld2307?._id   || null,
          others:         glMapping.deductions.others?._id         || null,
        },
      };
      await systemSettingsAPI.updatePaymentGLMapping(payload);
      setGlSaved(true);
      setTimeout(() => setGlSaved(false), 3000);
    } catch(e) { console.error(e); }
    finally { setGlSaving(false); }
  };

  return (
    <Layout pageTitle="System Settings">
      <div className="space-y-8 max-w-2xl">

        {/* ── Billing Settings ─────────────────────────────────────────── */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Billing Settings
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Billing Amount (₱)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={settings.minimumBillingAmount}
              onChange={e => setSettings(p => ({
                ...p,
                minimumBillingAmount: parseFloat(e.target.value) || 0
              }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              The minimum total amount per billing. If the sum of all line items
              is less than this value, the total will be set to this minimum.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trucking SOA Number Sequence (PSACC)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={soaYear}
                onChange={e => setSoaYear(e.target.value)}
                className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <span className="text-gray-400">—</span>
              <input
                type="number"
                min="0"
                step="1"
                value={settings.truckingSOACounters?.[soaYear] ?? 0}
                onChange={e => setSettings(p => ({
                  ...p,
                  truckingSOACounters: { ...p.truckingSOACounters, [soaYear]: parseInt(e.target.value, 10) || 0 },
                }))}
                className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Last SOA number issued for this year (e.g. "56" for 2026-056). The next Trucking bill
              will use the following number automatically, and this value updates as new bills are created.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
            {saved && (
              <span className="text-sm text-green-600 font-medium">✓ Settings saved</span>
            )}
          </div>
        </div>

        {/* ── Payment GL Mapping ────────────────────────────────────────── */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Payment GL Mapping
            </h2>
            {!glLoading && missingMappings.length > 0 && (
              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 font-medium">
                {missingMappings.length} account{missingMappings.length > 1 ? 's' : ''} not configured
              </span>
            )}
          </div>

          {!glLoading && missingMappings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
              <p className="font-medium mb-1">GL posting will fail until all accounts are mapped:</p>
              <ul className="list-disc list-inside text-xs space-y-0.5">
                {missingMappings.map(m => <li key={m}>{m}</li>)}
              </ul>
            </div>
          )}

          {glLoading ? (
            <div className="text-sm text-gray-400 py-4 text-center">Loading GL mapping…</div>
          ) : (
            <div className="space-y-6">
              {/* AR Account */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Accounts Receivable
                </h3>
                <AccountRow
                  label="AR Account (credited on payment)"
                  value={glMapping.arAccount}
                  onSelect={(acct) => setGlMapping(prev => ({ ...prev, arAccount: acct }))}
                />
              </div>

              {/* Payment Methods */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Payment Methods (debited)
                </h3>
                <div className="divide-y divide-gray-50">
                  <AccountRow label="Cash"          value={glMapping.paymentMethods.cash}         onSelect={a => setPaymentAccount('cash', a)} />
                  <AccountRow label="Bank Transfer"  value={glMapping.paymentMethods.bankTransfer} onSelect={a => setPaymentAccount('bankTransfer', a)} />
                  <AccountRow label="GCash"          value={glMapping.paymentMethods.gcash}        onSelect={a => setPaymentAccount('gcash', a)} />
                  <AccountRow label="Other / Check"  value={glMapping.paymentMethods.other}        onSelect={a => setPaymentAccount('other', a)} />
                </div>
              </div>

              {/* Deductions */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Deductions (debited)
                </h3>
                <div className="divide-y divide-gray-50">
                  <AccountRow label="Missing"          value={glMapping.deductions.missing}        onSelect={a => setDeductionAccount('missing', a)} />
                  <AccountRow label="Damage"           value={glMapping.deductions.damage}         onSelect={a => setDeductionAccount('damage', a)} />
                  <AccountRow label="Discount"         value={glMapping.deductions.discount}       onSelect={a => setDeductionAccount('discount', a)} />
                  <AccountRow label="General Expense"  value={glMapping.deductions.generalExpense} onSelect={a => setDeductionAccount('generalExpense', a)} />
                  <AccountRow label="Withheld 2307"    value={glMapping.deductions.withheld2307}   onSelect={a => setDeductionAccount('withheld2307', a)} />
                  <AccountRow label="Others"           value={glMapping.deductions.others}         onSelect={a => setDeductionAccount('others', a)} />
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button onClick={handleGLSave} disabled={glSaving}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 disabled:opacity-50">
                  {glSaving ? 'Saving…' : 'Save GL Mapping'}
                </button>
                {glSaved && (
                  <span className="text-sm text-green-600 font-medium">✓ GL mapping saved</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Waybill GL Mapping ────────────────────────────────────────── */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Waybill GL Mapping
            </h2>
            {!wbLoading && (!wbMapping.arAccount || !wbMapping.trucking.salesAccount || !wbMapping.freightforwarding.salesAccount) && (
              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 font-medium">
                Incomplete
              </span>
            )}
          </div>

          <p className="text-xs text-gray-400">
            These accounts are used to post revenue when a waybill is created. AR is debited; the sales account is credited.
          </p>

          {wbLoading ? (
            <div className="text-sm text-gray-400 py-4 text-center">Loading Waybill GL mapping…</div>
          ) : (
            <div className="space-y-6">
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Accounts Receivable (shared)
                </h3>
                <AccountRow
                  label="AR Account (debited on sale)"
                  value={wbMapping.arAccount}
                  onSelect={(acct) => setWbMapping(prev => ({ ...prev, arAccount: acct }))}
                />
              </div>

              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Sales Accounts (credited)
                </h3>
                <div className="divide-y divide-gray-50">
                  <AccountRow
                    label="Sales Account (Trucking)"
                    value={wbMapping.trucking.salesAccount}
                    onSelect={(acct) => setWbMapping(prev => ({
                      ...prev, trucking: { ...prev.trucking, salesAccount: acct },
                    }))}
                  />
                  <AccountRow
                    label="Stripping Expense Account (Trucking)"
                    value={wbMapping.trucking.strippingExpenseAccount}
                    onSelect={(acct) => setWbMapping(prev => ({
                      ...prev, trucking: { ...prev.trucking, strippingExpenseAccount: acct },
                    }))}
                  />
                  <AccountRow
                    label="Sales Account (Freight Forwarding)"
                    value={wbMapping.freightforwarding.salesAccount}
                    onSelect={(acct) => setWbMapping(prev => ({
                      ...prev, freightforwarding: { salesAccount: acct },
                    }))}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button onClick={handleWbGLSave} disabled={wbSaving}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 disabled:opacity-50">
                  {wbSaving ? 'Saving…' : 'Save Waybill GL Mapping'}
                </button>
                {wbSaved && (
                  <span className="text-sm text-green-600 font-medium">✓ Waybill GL mapping saved</span>
                )}
              </div>
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
}
